import React, { useState, useRef, useEffect } from 'react';
import ApiService from '../../../services/apiService';
import { toast } from 'react-toastify';
import userManagement from '../../../services/userManagement';

const PasscodePopup = ({ onSubmit }) => {
  const [values, setValues] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    // Create a portal-like container that bypasses event blocking
    const handleGlobalKeyDown = (e) => {
      // Only handle if one of our inputs is focused
      const activeElement = document.activeElement;
      const isOurInput = inputRefs.some((ref) => ref.current === activeElement);

      if (!isOurInput) return;

      const currentIndex = inputRefs.findIndex((ref) => ref.current === activeElement);
      if (currentIndex === -1) return;

      // Handle numeric input
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        const newValues = [...values];
        newValues[currentIndex] = e.key;
        setValues(newValues);

        // Auto-move to next empty input or next input
        const nextIndex = currentIndex + 1;
        if (nextIndex < 4) {
          setTimeout(() => {
            inputRefs[nextIndex].current?.focus();
          }, 50); // Increased delay for better reliability
        }
      }

      // Handle backspace
      else if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();

        const newValues = [...values];
        if (values[currentIndex]) {
          // Clear current input
          newValues[currentIndex] = '';
          setValues(newValues);
        } else if (currentIndex > 0) {
          // Move to previous input and clear it
          newValues[currentIndex - 1] = '';
          setValues(newValues);
          setTimeout(() => {
            inputRefs[currentIndex - 1].current?.focus();
          }, 50);
        }
      }

      // Handle Enter
      else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        const isComplete = values.every((v) => v !== '');
        if (isComplete) {
          handleSubmit();
        }
      }

      // Handle arrow keys for navigation
      else if (e.key === 'ArrowRight' && currentIndex < 3) {
        e.preventDefault();
        inputRefs[currentIndex + 1].current?.focus();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        e.preventDefault();
        inputRefs[currentIndex - 1].current?.focus();
      }
    };

    // Add global event listener
    document.addEventListener('keydown', handleGlobalKeyDown, true); // Use capture phase

    // Focus first input
    const timer = setTimeout(() => {
      inputRefs[0].current?.focus();
    }, 200);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
      clearTimeout(timer);
    };
  }, [values]);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const Token = localStorage.getItem('Token');
      if (!Token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const otpString = values.join('');

      if (otpString.length !== 4) {
        setError('Please enter a 4-digit passcode');
        setLoading(false);
        return;
      }

      let userId;
      try {
        userId = await userManagement.getUserId(Token);
        if (!userId) {
          const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
          userId = loginData.userId || loginData.data?.userId;
        }
      } catch (err) {
        console.error('Error getting user ID:', err);
        setError('Unable to retrieve user information');
        setLoading(false);
        return;
      }

      if (!userId) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      const apiService = new ApiService();
      const param = `?PinCode=${otpString}&UserId=${userId}`;
      const response = await apiService.getApiParamater('VERIFY_PASS', param);

      if (response && response.data && response.data.statusCode === 200) {
        toast.success('Passcode verified successfully');
        onSubmit(true);
      } else {
        const errorMessage = response?.data?.message || 'Invalid passcode';
        setError(errorMessage);
        setValues(['', '', '', '']);
        setTimeout(() => {
          inputRefs[0].current?.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Passcode verification error:', error);
      const errorMessage = error?.response?.data?.message || 'Error verifying passcode. Please try again.';
      setError(errorMessage);
      setValues(['', '', '', '']);
      setTimeout(() => {
        inputRefs[0].current?.focus();
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onSubmit(false);
  };

  const handleInputClick = (index) => {
    // Focus the clicked input
    inputRefs[index].current?.focus();
  };

  const isComplete = values.every((v) => v !== '');

  // Create number pad for mobile/touch interfaces
  const handleNumberClick = (number) => {
    // Find first empty input or use the focused one
    const activeElement = document.activeElement;
    const focusedIndex = inputRefs.findIndex((ref) => ref.current === activeElement);

    let targetIndex;
    if (focusedIndex !== -1 && !values[focusedIndex]) {
      targetIndex = focusedIndex;
    } else {
      targetIndex = values.findIndex((v) => v === '');
    }

    if (targetIndex !== -1) {
      const newValues = [...values];
      newValues[targetIndex] = number.toString();
      setValues(newValues);

      // Move to next input
      if (targetIndex < 3) {
        setTimeout(() => {
          inputRefs[targetIndex + 1].current?.focus();
        }, 50);
      }
    }
  };

  const handleBackspaceClick = () => {
    const lastFilledIndex = values
      .map((v, i) => (v ? i : -1))
      .filter((i) => i !== -1)
      .pop();
    if (lastFilledIndex !== undefined) {
      const newValues = [...values];
      newValues[lastFilledIndex] = '';
      setValues(newValues);

      setTimeout(() => {
        inputRefs[lastFilledIndex].current?.focus();
      }, 10);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.popup}>
        <h4 style={styles.title}>Enter 4 Digit Passcode</h4>
        <p style={styles.subtitle}>Please enter your passcode to unlock the system</p>

        {error && <p style={styles.error}>{error}</p>}

        {/* Passcode inputs */}
        <div style={styles.inputContainer}>
          {[0, 1, 2, 3].map((index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              value={values[index]}
              onClick={() => handleInputClick(index)}
              onFocus={() => {
                // Ensure proper focus handling
                setTimeout(() => {
                  if (inputRefs[index].current) {
                    inputRefs[index].current.setSelectionRange(1, 1);
                  }
                }, 10);
              }}
              // Remove onChange and onKeyDown - we handle via global listener
              readOnly // Prevent browser's default input handling
              style={{
                ...styles.otpInput,
                borderColor: error ? '#dc3545' : values[index] ? '#6E5199' : '#ddd',
                backgroundColor: values[index] ? '#f8f9fa' : 'white',
              }}
              tabIndex={index + 1}
              data-index={index}
            />
          ))}
        </div>

        {/* Virtual number pad */}
        <div style={styles.numberPad}>
          <div style={styles.numberRow}>
            {[1, 2, 3].map((num) => (
              <button key={num} type="button" onClick={() => handleNumberClick(num)} style={styles.numberButton} disabled={loading}>
                {num}
              </button>
            ))}
          </div>
          <div style={styles.numberRow}>
            {[4, 5, 6].map((num) => (
              <button key={num} type="button" onClick={() => handleNumberClick(num)} style={styles.numberButton} disabled={loading}>
                {num}
              </button>
            ))}
          </div>
          <div style={styles.numberRow}>
            {[7, 8, 9].map((num) => (
              <button key={num} type="button" onClick={() => handleNumberClick(num)} style={styles.numberButton} disabled={loading}>
                {num}
              </button>
            ))}
          </div>
          <div style={styles.numberRow}>
            <button type="button" onClick={handleBackspaceClick} style={{ ...styles.numberButton, backgroundColor: '#dc3545' }} disabled={loading}>
              ⌫
            </button>
            <button type="button" onClick={() => handleNumberClick(0)} style={styles.numberButton} disabled={loading}>
              0
            </button>
            <button
              type="button"
              onClick={() => setValues(['', '', '', ''])}
              style={{ ...styles.numberButton, backgroundColor: '#6E5199', color: '#fff' }}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>

        <div style={styles.buttonContainer}>
          <button
            type="button"
            onClick={handleSubmit}
            style={{
              ...styles.submitButton,
              opacity: !isComplete || loading ? 0.6 : 1,
              cursor: !isComplete || loading ? 'not-allowed' : 'pointer',
            }}
            disabled={!isComplete || loading}
          >
            {loading ? 'Verifying...' : 'Unlock'}
          </button>

          <button type="button" style={styles.cancelButton} onClick={handleCancel} disabled={loading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10001,
  },
  popup: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
  },
  title: {
    marginBottom: '10px',
    color: '#333',
    fontSize: '20px',
    fontWeight: 'bold',
  },
  subtitle: {
    marginBottom: '20px',
    color: '#666',
    fontSize: '14px',
  },
  error: {
    color: '#dc3545',
    marginBottom: '15px',
    fontSize: '14px',
    fontWeight: '500',
  },
  inputContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  otpInput: {
    width: '50px',
    height: '50px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    border: '2px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
  },
  numberPad: {
    marginBottom: '20px',
  },
  numberRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  numberButton: {
    minWidth: '50px',
    height: '50px',
    fontSize: '18px',
    fontWeight: 'bold',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  submitButton: {
    padding: '12px 20px',
    backgroundColor: '#6E5199',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
};

export default PasscodePopup;
